import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Wrench, Phone, Mail, MapPin, Euro, Camera, Check, 
  ArrowRight, Shield, Clock, FileText
} from 'lucide-react';

export default function VendreMateriel() {
  const { i18n } = useTranslation();
  const isFr = i18n.language === 'fr';
  const { API } = useContext(AppContext);
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    telephone: '',
    email: '',
    type_materiel: '',
    localisation: '',
    prix_souhaite: '',
    description: '',
    rgpd_accepted: false,
    commissions_accepted: false,
  });

  const typesMateriels = [
    { value: 'foreuse', label: isFr ? 'Foreuse géotechnique' : 'Geotechnical drill rig' },
    { value: 'pompe', label: isFr ? 'Pompe / Centrale hydraulique' : 'Pump / Hydraulic unit' },
    { value: 'compresseur', label: isFr ? 'Compresseur' : 'Compressor' },
    { value: 'outil_forage', label: isFr ? 'Outils de forage' : 'Drilling tools' },
    { value: 'instrumentation', label: isFr ? 'Instrumentation (Jean Lutz, etc.)' : 'Instrumentation' },
    { value: 'piezometre', label: isFr ? 'Piézomètre / Inclinomètre' : 'Piezometer / Inclinometer' },
    { value: 'penetrometre', label: isFr ? 'Pénétromètre (SPT/CPT)' : 'Penetrometer (SPT/CPT)' },
    { value: 'injection', label: isFr ? 'Matériel d\'injection' : 'Injection equipment' },
    { value: 'transport', label: isFr ? 'Engin de transport / Manutention' : 'Transport / Handling' },
    { value: 'autre', label: isFr ? 'Autre matériel' : 'Other equipment' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.rgpd_accepted || !formData.commissions_accepted) {
      toast.error(isFr ? 'Veuillez accepter les conditions' : 'Please accept the conditions');
      return;
    }

    setLoading(true);
    
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '' && formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      await axios.post(`${API}/demandes/vente`, formDataToSend);
      setSubmitted(true);
      toast.success(isFr ? 'Demande envoyée avec succès !' : 'Request sent successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(isFr ? 'Erreur lors de l\'envoi' : 'Error sending request');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-[#0A2540] mb-4">
            {isFr ? 'Merci !' : 'Thank you!'}
          </h1>
          <p className="text-gray-600 mb-6">
            {isFr 
              ? 'Votre demande a bien été reçue. Nous vous rappelons sous 24h pour évaluer votre matériel.'
              : 'Your request has been received. We\'ll call you back within 24h to evaluate your equipment.'}
          </p>
          <div className="flex items-center justify-center gap-2 text-[#635BFF] font-medium">
            <Phone className="w-5 h-5" />
            <span>+33 7 82 93 93 83</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero */}
      <section className="pt-16 pb-8 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 bg-blue-100 rounded-full px-4 py-2 mb-6">
            <Wrench className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-600">
              {isFr ? 'Vente de matériel' : 'Equipment sale'}
            </span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-4">
            {isFr ? 'Vendez votre matériel géotechnique' : 'Sell your geotechnical equipment'}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {isFr 
              ? 'Remplissez ce formulaire en 2 minutes. On s\'occupe de tout : photos pro, annonce, visites, négociation.'
              : 'Fill this form in 2 minutes. We handle everything: pro photos, listing, visits, negotiation.'}
          </p>
        </div>
      </section>

      {/* Trust badges */}
      <section className="pb-8 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-green-500" />
              {isFr ? 'Sans engagement' : 'No commitment'}
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-500" />
              {isFr ? 'Réponse sous 24h' : 'Response within 24h'}
            </div>
            <div className="flex items-center gap-2">
              <Euro className="w-4 h-4 text-amber-500" />
              {isFr ? 'Commission 4-12%' : '4-12% commission'}
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="pb-16 px-4">
        <div className="container mx-auto max-w-2xl">
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100">
            <div className="space-y-6">
              {/* Nom */}
              <div>
                <Label htmlFor="nom" className="text-[#0A2540] font-medium">
                  {isFr ? 'Nom complet' : 'Full name'} *
                </Label>
                <Input
                  id="nom"
                  type="text"
                  required
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  placeholder={isFr ? 'Jean Dupont' : 'John Doe'}
                  className="mt-2"
                />
              </div>

              {/* Téléphone */}
              <div>
                <Label htmlFor="telephone" className="text-[#0A2540] font-medium">
                  {isFr ? 'Téléphone' : 'Phone'} *
                </Label>
                <Input
                  id="telephone"
                  type="tel"
                  required
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  placeholder="+33 6 12 34 56 78"
                  className="mt-2"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-[#0A2540] font-medium">
                  Email <span className="text-gray-400 font-normal">({isFr ? 'optionnel' : 'optional'})</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="jean@exemple.com"
                  className="mt-2"
                />
              </div>

              {/* Type de matériel */}
              <div>
                <Label className="text-[#0A2540] font-medium">
                  {isFr ? 'Type de matériel' : 'Equipment type'} *
                </Label>
                <Select
                  value={formData.type_materiel}
                  onValueChange={(value) => setFormData({ ...formData, type_materiel: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder={isFr ? 'Sélectionnez le type' : 'Select type'} />
                  </SelectTrigger>
                  <SelectContent>
                    {typesMateriels.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Localisation */}
              <div>
                <Label htmlFor="localisation" className="text-[#0A2540] font-medium">
                  {isFr ? 'Localisation (Pays + Code postal)' : 'Location (Country + Postal code)'} *
                </Label>
                <Input
                  id="localisation"
                  type="text"
                  required
                  value={formData.localisation}
                  onChange={(e) => setFormData({ ...formData, localisation: e.target.value })}
                  placeholder={isFr ? 'France, 69001' : 'France, 69001'}
                  className="mt-2"
                />
              </div>

              {/* Prix souhaité */}
              <div>
                <Label htmlFor="prix" className="text-[#0A2540] font-medium">
                  {isFr ? 'Prix souhaité (€HT)' : 'Desired price (€excl.)'} <span className="text-gray-400 font-normal">({isFr ? 'optionnel' : 'optional'})</span>
                </Label>
                <Input
                  id="prix"
                  type="number"
                  value={formData.prix_souhaite}
                  onChange={(e) => setFormData({ ...formData, prix_souhaite: e.target.value })}
                  placeholder="50000"
                  className="mt-2"
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description" className="text-[#0A2540] font-medium">
                  {isFr ? 'Description courte' : 'Short description'} <span className="text-gray-400 font-normal">({isFr ? 'optionnel' : 'optional'})</span>
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={isFr ? 'Marque, modèle, année, heures, état...' : 'Brand, model, year, hours, condition...'}
                  className="mt-2"
                  rows={3}
                />
              </div>

              {/* Checkboxes */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="commissions"
                    checked={formData.commissions_accepted}
                    onCheckedChange={(checked) => setFormData({ ...formData, commissions_accepted: checked })}
                  />
                  <Label htmlFor="commissions" className="text-sm text-gray-600 cursor-pointer leading-relaxed">
                    {isFr 
                      ? "J'accepte la grille de commissions (4-12% selon le prix de vente)"
                      : "I accept the commission grid (4-12% depending on sale price)"}
                    <a href="/commissions" className="text-[#635BFF] hover:underline ml-1">
                      {isFr ? 'Voir les tarifs' : 'View rates'}
                    </a>
                  </Label>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="rgpd"
                    checked={formData.rgpd_accepted}
                    onCheckedChange={(checked) => setFormData({ ...formData, rgpd_accepted: checked })}
                  />
                  <Label htmlFor="rgpd" className="text-sm text-gray-600 cursor-pointer leading-relaxed">
                    {isFr 
                      ? "J'accepte le traitement de mes données personnelles conformément à la politique de confidentialité"
                      : "I accept the processing of my personal data in accordance with the privacy policy"}
                  </Label>
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading || !formData.rgpd_accepted || !formData.commissions_accepted}
                className="w-full bg-[#635BFF] hover:bg-[#5248d9] text-white py-4 text-lg font-semibold"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    {isFr ? 'Envoi en cours...' : 'Sending...'}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {isFr ? 'Envoyer ma demande' : 'Send my request'}
                    <ArrowRight className="w-5 h-5" />
                  </span>
                )}
              </Button>
            </div>
          </form>

          {/* Note */}
          <p className="text-center text-sm text-gray-500 mt-6">
            {isFr 
              ? "Pas de compte à créer. On vous rappelle directement."
              : "No account needed. We'll call you directly."}
          </p>
        </div>
      </section>
    </div>
  );
}
